import { EventTypes, Node, useEditor } from '@candulabs/craft';
import { styled } from 'canoe';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { ContentType } from '@candu-ai/content/types';
import { getNodeId } from '@candu-ai/styleguide/selectors';
import { useActiveTooltip, useTooltipChildren } from 'craft/components/Tooltip';
import { BuiltInComponents, CRAFT_ROOT_ID } from 'craft/constants';
import { useAddTooltipStep, useNodeContext, useSelection, useTooltipPlacement } from 'craft/hooks';
import {
  setActiveTooltip,
  setForcePlacementsTab,
  setToolboxTab,
  useActiveTooltipFromRedux,
  useToolboxState,
  useToolboxTab,
} from 'craft/state';
import { Resolver, ToolboxTabs } from 'craft/types';

const GroupWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  z-index: 0;
  overflow: hidden;
`;

const Backdrop = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 1;
`;

export const HotspotGroup: Resolver = ({ children, ...otherProps }: any) => {
  const { id } = useNodeContext();
  const { actions } = useEditor();
  const { selection } = useSelection();
  const hotspotNodeIds = useTooltipChildren();
  const { addFirst } = useAddTooltipStep(ContentType.HotspotGroup);
  const activeHotspot = useActiveTooltip();
  const activeHotspotFromRedux = useActiveTooltipFromRedux();
  const { placement } = useTooltipPlacement(activeHotspot?.id);
  const { activeTab } = useToolboxTab();
  const { forceOpenToolbox } = useToolboxState();
  const dispatch = useDispatch();

  useEffect(() => {
    // on first content load, set first hotspot as active
    if (!activeHotspot?.id) {
      dispatch(setActiveTooltip(hotspotNodeIds[0]));
      return;
    }

    // when ROOT is selected, make active hotspot selected
    if (selection.first === CRAFT_ROOT_ID) {
      actions.setEventNodes(EventTypes.selected, [activeHotspotFromRedux || hotspotNodeIds[0]]);
      return;
    }

    // safeguard to make sure the right active hotspot is saved in store
    if (activeHotspot.id !== activeHotspotFromRedux) {
      dispatch(setActiveTooltip(activeHotspot.id));
    }
  }, [activeHotspot?.id, activeHotspotFromRedux, selection.first]);

  useEffect(() => {
    const hotspotHasNoPlacement = !!activeHotspot?.id && !placement;
    dispatch(setForcePlacementsTab(hotspotHasNoPlacement));
    if (hotspotHasNoPlacement) {
      // if we're forcing the placements tab then make it the active tab, otherwise don't change
      if (activeTab !== ToolboxTabs.Placements) {
        dispatch(setToolboxTab(ToolboxTabs.Placements));
      }
      // when forcing placements tab, we want to show the toolbox
      forceOpenToolbox();
    }
  }, [placement, activeHotspot?.id]);

  // if we have no children, add the first one
  const childNodes = children?.props?.children || [];

  useEffect(() => {
    if (childNodes.length === 0) {
      addFirst();
    }
  }, [childNodes]);

  if (!activeHotspot) {
    return null;
  }

  return (
    <GroupWrapper id={getNodeId(id)} {...otherProps} data-nid={id} data-canvas="true">
      <Backdrop data-craft-ignore-events id="hotspotGroupBackdrop" />
      {childNodes}
    </GroupWrapper>
  );
};

HotspotGroup.craft = {
  name: BuiltInComponents.HotspotGroup,
  rules: {
    canMoveIn: (incomingNodes: Node[]) =>
      incomingNodes.every((node) => node.data.name === BuiltInComponents.Hotspot),
  },
  displayName: 'Hotspot Group',
};
